import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'tamagui';
import dayjs from 'dayjs';
import { formatDateTime, isSameDayUtil } from '../../utils/dateTime';

import { CobuStack, CobuText, CobuImage } from '@cobu/ui';
import { Gathering, RSVP, UserInfo } from '@cobu/types';
import { URLS } from '../../constants/Urls';
import { Strings } from '../../constants/Strings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreatorMenuButton from './CreatorMenuButton';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CalendarButton from '../ui/CalendarButton';

interface EventDetailHeaderProps {
  gathering: Gathering;
  rsvps?: RSVP[];
  currentUser: UserInfo | null;
  isCurrentUserCreator: boolean;
  totalAttendance: number;
  onWhosComingPress?: () => void;
  onEditPress: () => void;
  onCancelGatheringPress: () => void;
  onBlockUserPress: () => void;
  onReportPress: () => void;
  canReport?: boolean;
}

export function EventDetailHeader({
  gathering,
  rsvps = [],
  currentUser,
  totalAttendance,
  isCurrentUserCreator,
  onWhosComingPress,
  onEditPress,
  onCancelGatheringPress,
  onBlockUserPress,
  onReportPress,
  canReport = true,
}: EventDetailHeaderProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const strings = Strings.get();

  const formatEventDate = (scheduledStartTime: string, scheduledEndTime?: string) => {
    const tz = gathering.buildingTimezone || 'UTC';
    const date = formatDateTime(scheduledStartTime, tz, 'MMMM D, YYYY');
    const startTimeStr = formatDateTime(scheduledStartTime, tz, 'h:mm A');
    const time = scheduledEndTime
      ? `${startTimeStr} - ${formatDateTime(scheduledEndTime, tz, 'h:mm A')}`
      : startTimeStr;
    return { date, time };
  };

  const { date, time } = formatEventDate(gathering.scheduledStartTime, gathering.scheduledEndTime);
  const isRecurringOrPartner = !!gathering.recurringType || !!gathering.isPartnerEvent;
  const effectiveEndTime = gathering.scheduledEndTime
    ? dayjs(gathering.scheduledEndTime)
    : isRecurringOrPartner
      ? dayjs(gathering.scheduledStartTime).add(1, 'hour')
      : null;
  const isEventLive = !!effectiveEndTime && dayjs().isAfter(gathering.scheduledStartTime) && dayjs().isBefore(effectiveEndTime);
  const isEventPast = !!effectiveEndTime && dayjs().isAfter(effectiveEndTime);
  const isResidentOrganizedEvent =
    gathering.eventCreationUuid === '43bf1791-3191-4675-b4aa-0d41de217d6f';

  const [showDirections, setShowDirections] = useState(false);

  const formatPostalAddress = (addr: Gathering['postalAddress']): string | null => {
    if (!addr) return null;
    const lines = [addr.addressLine1, addr.addressLine2, addr.addressLine3]
      .filter(Boolean)
      .join(', ');
    const cityStateZip = [
      addr.locality,
      [addr.administrativeArea, addr.postalCode].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join(', ');
    return [lines, cityStateZip].filter(Boolean).join(', ') || null;
  };

  const handleCreatorPress = () => {
    if (
      currentUser?.building?.uuid === gathering.creator?.building &&
      !gathering.creator?.deactivated
    ) {
      navigate(`/profile/${gathering?.creator?.uuid}`);
    }
  };

  return (
    <CobuStack bg="$background">
      {renderEventImage()}

      {/* Event Info */}
      <CobuStack px="$4" gap="$1">
        <CobuStack layout="horizontal" ai="center" jc="space-between">
          {/* Event Title */}
          <CobuText variant="heading" fos="$6" fow="bold" pt="$3" numberOfLines={2} col="$cobuDarkBlue">
            {gathering.title}
          </CobuText>

        {/* Creator actions menu */}
        <CreatorMenuButton
          isCurrentUserCreator={isCurrentUserCreator}
          onEditPress={onEditPress}
          onCancelGatheringPress={onCancelGatheringPress}
          onBlockUserPress={onBlockUserPress}
          onReportPress={onReportPress}
          canReport={canReport}
          title={gathering.title}
        />
        </CobuStack>

        {renderDateTime()}

        {(gathering.partnerEventType || gathering.partnerOtherEventType) && (
          <CobuText variant="body" fos="$2" col="$colorMuted">
            {gathering.partnerEventType || gathering.partnerOtherEventType}
          </CobuText>
        )}
        {gathering.partnerFrequency && (
          <CobuText variant="body" fos="$2" col="$colorMuted">
            {gathering.partnerFrequency} · {strings.events.detail.labels.recurringIndicator}
          </CobuText>
        )}

        {dayjs().isBefore(gathering.scheduledStartTime) && (
          <CobuStack pt="$3">
            <CalendarButton gathering={gathering} />
          </CobuStack>
        )}
        {renderCreatorInfo()}

        {/* Meta Details */}
        {renderMetaDetails()}

        {/* Event Status */}
        {gathering.cancelled && (
          <CobuStack bg="$backgroundMuted" br="$2" px="$3" py="$2" ai="center">
            <CobuText color="$red" fos="$3" fow="bold">
              {strings.events.detail.labels.gatheringCancelled}
            </CobuText>
          </CobuStack>
        )}
      </CobuStack>
    </CobuStack>
  );

  function renderEventImage() {
    return (
      <CobuStack h="$56" bg="$cobuDarkBlue" pos="relative">
        <CobuImage
          source={{ uri: gathering.image || gathering.groupImage || URLS.DEFAULT_PROFILE_IMAGE }}
          width="100%"
          height="100%"
        />

        {isResidentOrganizedEvent && (
          <CobuStack
            pos="absolute"
            b="$5"
            px="$3"
            py="$2"
            r="$0"
            bg="$cobuTopaz"
            btlr="$4"
            bblr="$4"
          >
            <CobuText color="$white" fos="$2" fow="500">
              {strings.events.detail.labels.residentOrganized}
            </CobuText>
          </CobuStack>
        )}
      </CobuStack>
    );
  }

  function renderDateTime() {
    const startDate = formatDateTime(
      gathering.scheduledStartTime,
      gathering.buildingTimezone,
      'ddd, MMM DD, h:mm A'
    );

    const isSameDay = gathering.scheduledEndTime
      ? isSameDayUtil(gathering.scheduledStartTime, gathering.scheduledEndTime)
      : false;

    const endDate = gathering.scheduledEndTime
      ? formatDateTime(
          gathering.scheduledEndTime,
          gathering.buildingTimezone,
          isSameDay ? 'h:mm A' : 'ddd, MMM DD, h:mm A'
        )
      : null;

    const startTimeDay = `${startDate.split(',')[0]},`;
    const startTimeDate = startDate.split(',')[1];
    const startTimeTime = startDate.split(',')[2];
    const endTimeDay = endDate ? endDate.split(',')[0] : null;
    const endTimeDate = endDate ? endDate.split(',')[1] : null;
    const endTimeTime = endDate ? endDate.split(',')[2] : null;

    return (
      <CobuStack>
        <CobuText variant="body" fos="$3" fow="500" col="$cobuDarkBlue">
          {startTimeDay}
          <CobuText variant="body" col="$cobuPink" fos="$3" fow="bold">
            {startTimeDate}
          </CobuText>
          {startTimeTime}
          {endDate && (
            <>
              {` - `}
              {endTimeDay}
              {endTimeDate && (
                <CobuText variant="body" col="$cobuPink" fos="$3" fow="bold">
                  ,{endTimeDate}
                </CobuText>
              )}
              {endTimeTime}
            </>
          )}
        </CobuText>
      </CobuStack>
    );
  }

  function renderTextAvatar(creator: { uuid: string; displayName: string }) {
    const avatarColors = [
      { backgroundColor: '#44C0D4', color: '#FFFFFF' },
      { backgroundColor: '#4A6BDF', color: '#FFFFFF' },
      { backgroundColor: '#F15185', color: '#FFFFFF' },
      { backgroundColor: '#FDD019', color: 'rgba(0, 0, 0, 0.5)' },
      { backgroundColor: '#FEAE35', color: 'rgba(0, 0, 0, 0.5)' },
      { backgroundColor: '#4ED293', color: '#FFFFFF' },
      { backgroundColor: '#A56BEE', color: '#FFFFFF' },
      { backgroundColor: '#EF4A4A', color: '#FFFFFF' },
    ];

    const colorIndex = creator.uuid.charCodeAt(0) % 8;
    const { backgroundColor, color } = avatarColors[colorIndex];

    // Get initials from displayName
    const initials = creator.displayName
      .split(' ')
      .slice(0, 2)
      .map(name => name.charAt(0).toUpperCase())
      .join('');

    return (
      <CobuStack w="$10" h="$10" br="$full" ai="center" jc="center" style={{ backgroundColor }}>
        <CobuText variant="body" fos="$3" fow="bold" style={{ color }}>
          {initials}
        </CobuText>
      </CobuStack>
    );
  }

  function renderCreatorInfo() {
    if (!gathering.creator) return null;

    return (
      <CobuStack onPress={handleCreatorPress} w="100%">
        <CobuStack layout="horizontal" ai="center" gap="$3" py="$3" br="$3">
          {/* Creator Profile Image or Text Avatar */}
          {gathering.creator.profileImage &&
          gathering.creator.profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
            <CobuImage
              source={{ uri: gathering.creator.profileImage }}
              width={40}
              height={40}
              br="$full"
              ov="hidden"
            />
          ) : (
            renderTextAvatar(gathering.creator)
          )}

          {/* Creator Info */}
          <CobuStack f={1} gap="$1">
            <CobuText variant="body" fos="$3" fow="600" numberOfLines={1}>
              {gathering.creator.displayName}
            </CobuText>
            <CobuText variant="body" fos="$2" fow="300" color="$colorMuted">
              {strings.events.detail.labels.gatheringCreator}
            </CobuText>
          </CobuStack>
        </CobuStack>
      </CobuStack>
    );
  }

  function renderUsers() {
    if (!Array.isArray(rsvps) || rsvps.length < 1) return null;

    return (
      <CobuStack gap="$2">
        {/* Attendance Count */}
        <CobuStack onPress={onWhosComingPress}>
          <CobuStack layout="horizontal" ai="center" gap="$3">
            <CheckCircleIcon style={{ color: '$cobuTeal', fontSize: 18 }} />
            <CobuText variant="body" fos="$1" fow="bold" col="$cobuDarkBlue">
              {totalAttendance} {strings.events.detail.labels.attendingCount}
            </CobuText>
          </CobuStack>
        </CobuStack>

        {/* User RSVP Status */}
        {gathering.isAttending && (
          <CobuStack onPress={onWhosComingPress}>
            <CobuText variant="body" fos="$2" fow="bold" col="$cobuDarkBlue">
              {strings.events.detail.labels.userRsvpConfirmation}
            </CobuText>
          </CobuStack>
        )}
      </CobuStack>
    );
  }

  function renderMetaDetails() {
    const { locationString, interests, whatToBring, postalAddress, drivingDirections, rateDetails } = gathering;

    const handleLocationPress = (url: string) => {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank');
      }
    };

    const formattedAddress = formatPostalAddress(postalAddress);

    return (
      <CobuStack gap="$1">
        {/* Location Details */}
        {(locationString || formattedAddress) && (
          <CobuStack layout="horizontal" ai="flex-start" gap="$3">
            <LocationOnOutlinedIcon style={{ color: '$cobuTeal', fontSize: 18, marginTop: 2 }} />
            <CobuStack f={1} layout="horizontal" ai="center" flexWrap="wrap" gap="$2">
              {locationString && (
                <CobuStack onPress={() => handleLocationPress(locationString)}>
                  <CobuText
                    variant="body"
                    fos="$1"
                    col={locationString.includes('http') ? '$cobuTeal' : '$color'}
                    style={{
                      textDecorationLine: locationString.includes('http') ? 'underline' : 'none',
                      cursor: locationString.includes('http') ? 'pointer' : 'default',
                    }}
                  >
                    {locationString}
                  </CobuText>
                </CobuStack>
              )}
              {formattedAddress && (
                <CobuText variant="body" fos="$1" fow="300" col="$color">
                  {formattedAddress}
                </CobuText>
              )}
              {drivingDirections && (
                <CobuText
                  variant="body"
                  fos="$1"
                  col="$cobuTeal"
                  style={{ textDecorationLine: 'underline', cursor: 'pointer' }}
                  onPress={() => setShowDirections(true)}
                >
                  {Strings.get().events.detail.labels.directions}
                </CobuText>
              )}
            </CobuStack>
          </CobuStack>
        )}

        {/* Directions dialog */}
        {showDirections && (
          <CobuStack
            pos="fixed"
            t="$0"
            l="$0"
            r="$0"
            b="$0"
            style={{ zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' }}
            ai="center"
            jc="center"
            onPress={() => setShowDirections(false)}
          >
            <CobuStack
              bg="$background"
              br="$4"
              p="$5"
              mx="$4"
              gap="$3"
              style={{ maxWidth: 400, width: '100%' }}
              onPress={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <CobuText variant="heading" fos="$5" fow="bold" col="$cobuDarkBlue">
                {Strings.get().events.detail.labels.drivingDirections}
              </CobuText>
              <CobuText variant="body" fos="$3" col="$color">
                {drivingDirections}
              </CobuText>
              <CobuText
                variant="body"
                fos="$3"
                col="$cobuTeal"
                fow="bold"
                style={{ cursor: 'pointer', alignSelf: 'flex-end' }}
                onPress={() => setShowDirections(false)}
              >
                {Strings.get().events.detail.labels.close}
              </CobuText>
            </CobuStack>
          </CobuStack>
        )}

        {/* Rate Details */}
        {rateDetails?.totalAmount != null && rateDetails.totalAmount > 0 && (
          <CobuText variant="body" fos="$2" col="$colorMuted">
            {strings.events.detail.labels.priceFrom} ${rateDetails.totalAmount.toFixed(2)}
          </CobuText>
        )}

        {/* Interests */}
        {interests && interests.length > 0 && (
          <CobuStack layout="horizontal" ai="flex-start" gap="$3">
            <CobuStack f={1}>
              <CobuText variant="body" fos="$3">
                {interests.map((interest: { name: string }) => interest.name).join(', ')}
              </CobuText>
            </CobuStack>
          </CobuStack>
        )}

        {/* Users/Attendance */}
        {renderUsers()}

        {/* What to Bring */}
        {whatToBring && whatToBring.trim() !== '' && (
          <CobuStack my="$4" py="$2" px="$4" bg="$cobuDarkBlue10" br="$3">
            <CobuText variant="heading" fos="$4" fow="bold" mb="$2" col="$cobuDarkBlue">
              {strings.events.detail.labels.whatToBring}
            </CobuText>
            <CobuText variant="body" fos="$3" col="$cobuDarkBlue">
              {whatToBring}
            </CobuText>
          </CobuStack>
        )}

        {isResidentOrganizedEvent && (
          <CobuText variant="body" fos="$3">
            {strings.events.detail.labels.residentOrganizedDisclaimer}
          </CobuText>
        )}
      </CobuStack>
    );
  }
}
